'use client';

/**
 * The chat window (docs/CHAT.md), the way Reddit's chat sits on the page:
 *
 *   docked       bottom right, the list of chats beside the open one
 *   full window  the button in its header (⤢) makes it fill the window under the
 *                navbar; again (⤡) puts it back
 *   narrow       on a phone, or whenever the window has no room for two panes,
 *                one pane at a time: the list, or a chat with ← back
 *
 * Views in the right pane: a chat · New chat · Create a group · group settings
 * · a chat request · chat settings (⚙: the window's look, blocked accounts).
 *
 * Anyone can send anyone a chat request; what two people already share (they
 * played co-op together) is shown as a badge on the chat, the request and the
 * people in New chat. The window can be Light, Dark or Auto (as the site),
 * on its own: `.dark` / `.gt-light` around it (tailwind.config.js). It is drawn at the top of the page (Layer),
 * so nothing on the page can sit over it, and it keeps its state between pages.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import LiveTiger from '../world/LiveTiger';   // a face for whoever has no picture (owner 2026-11-03)
import './chat-ga.css';   // the World's chat: the Gilded Arcade look (owner 2026-11-03)
import Layer from '../Layer';
import { Avatar, RichText } from '../camps/bits';
import type { Person } from '@/lib/campsApi';
import {
  api, useChat, setDock, openRoom, refreshRooms, loadLatest, loadOlder, sendMessage, sayTyping, dropFailed, applyEdited,
  roomTitle, setChatTheme, ChatApiError, type ChatRoom, type ChatMessage, type Bond, type Contact, type ChatTheme,
} from '@/lib/chat';
import { askConfirm } from '@/lib/confirm';
import { usePostTranslation, TrLine, TranslateGlyph } from '../world/PostTranslate';

const I = ({ d, className = 'h-[18px] w-[18px]' }: { d: string; className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d={d} /></svg>
);
const ICON = {
  plus: 'M12 5v14M5 12h14', close: 'M6 6l12 12M18 6L6 18', back: 'M15 6l-6 6 6 6', more: 'M5 12h.01M12 12h.01M19 12h.01',
  expand: 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5', shrink: 'M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5', min: 'M5 12h14',
  send: 'M4 12l16-8-6 16-2.5-6.5z', group: 'M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM2.5 20c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5M16 4.3a3.5 3.5 0 010 6.4M18 14.8c2.1.7 3.5 2.4 3.5 5.2',
  reply: 'M9 7L4 12l5 5M4 12h11a5 5 0 015 5v1',
  gear: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z',
  sun: 'M12 16a4 4 0 100-8 4 4 0 000 8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  moon: 'M20 14.5A8 8 0 019.5 4 8 8 0 1020 14.5z',
  auto: 'M12 3a9 9 0 100 18V3zM12 3a9 9 0 010 18',
  coop: 'M6 11h4M8 9v4M15 12h.01M18 10h.01M17.3 5H6.7a4 4 0 00-3.9 3.2l-1 5.3A2.8 2.8 0 006.6 15l1.9-2h7l1.9 2a2.8 2.8 0 004.8-1.5l-1-5.3A4 4 0 0017.3 5z',
};

/** The stamp's marks: sending, sent (✓), read (✓✓). */
const TICK = {
  wait: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 7v5l3 2',
  sent: 'M4.5 12.5l4.5 4.5L19.5 6.5',
  read: 'M1.5 12.5l4.5 4.5L16.5 6.5M10 15.5l1.5 1.5L22 6.5',
};

/** What two people share, as a short line: "Played co-op · 3 missions". */
const bondText = (b: Bond | null | undefined) => (b && b.games > 0 ? `Played co-op · ${b.games} ${b.games === 1 ? 'mission' : 'missions'}` : '');
function BondChip({ bond, className = '' }: { bond: Bond | null | undefined; className?: string }) {
  const t = bondText(bond);
  if (!t) return null;
  return (
    <span title={`You have played ${bond!.games} online co-op ${bond!.games === 1 ? 'mission' : 'missions'} together${bond!.clears ? ` and cleared ${bond!.clears}` : ''}.`}
          className={'inline-flex max-w-full items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 ' + className}>
      <I d={ICON.coop} className="h-3 w-3 shrink-0" /><span className="truncate">{t}</span>
    </span>
  );
}
const BTN = 'grid h-8 w-8 shrink-0 place-items-center rounded-full text-ink-600 transition hover:bg-ink-100 hover:text-ink-900 dark:text-ink-300 dark:hover:bg-white/[0.08] dark:hover:text-white';
const PRIMARY = 'inline-flex min-h-[38px] items-center justify-center gap-2 rounded-full bg-accent-600 px-4 text-[13.5px] font-semibold text-white transition hover:bg-accent-700 disabled:opacity-50';
const QUIET = 'inline-flex min-h-[38px] items-center justify-center gap-2 rounded-full px-4 text-[13.5px] font-semibold text-ink-700 ring-1 ring-ink-200 transition hover:bg-ink-50 dark:text-ink-200 dark:ring-white/15 dark:hover:bg-white/[0.06]';
const INPUT = 'w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[14px] text-ink-900 placeholder:text-ink-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400/20 dark:border-white/10 dark:bg-ink-950 dark:text-ink-50';

const clock = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const dayOf = (iso: string) => {
  const d = new Date(iso), now = new Date();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
};
/**
 * One run of messages: the same person, text after text, on the same day, each
 * within 5 minutes of the one before. As in Telegram (owner, 2026-10-15), a run
 * draws one avatar, beside its last message, whose bubble gets the "tail"
 * corner; every bubble carries its own time in its bottom-right corner.
 */
const RUN_GAP = 5 * 60_000;
const sameRun = (a: ChatMessage | undefined, b: ChatMessage | undefined): boolean =>
  !!a && !!b && a.kind === 'text' && b.kind === 'text' && a.author?.id === b.author?.id
  && dayOf(a.at) === dayOf(b.at) && Math.abs(+new Date(b.at) - +new Date(a.at)) < RUN_GAP;
const shortAgo = (iso: string) => {
  const s = (Date.now() - +new Date(iso)) / 1000;
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/** A chat's picture: the other person, or a group's first two faces. */
function RoomFace({ room, size = 40 }: { room: ChatRoom; size?: number }) {
  if (room.kind === 'dm') return <Face person={room.people[0] ?? null} size={size} />;
  const [a, b] = room.people;
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      <span className="absolute left-0 top-0 rounded-full ring-2 ring-white dark:ring-ink-900"><Face person={a ?? null} size={size * 0.68} /></span>
      <span className="absolute bottom-0 right-0 rounded-full ring-2 ring-white dark:ring-ink-900">{b ? <Face person={b} size={size * 0.68} /> : <span className="grid place-items-center rounded-full bg-ink-200 text-ink-600 dark:bg-ink-700 dark:text-ink-200" style={{ width: size * 0.68, height: size * 0.68 }}><I d={ICON.group} className="h-3.5 w-3.5" /></span>}</span>
    </span>
  );
}

// ── The window ───────────────────────────────────────────────────────────────

/**
 * One window per page. A page can hold two navbars at once (a game open over
 * the Games shelf), and each carries a chat button: the first one mounted draws
 * the window, the others draw nothing.
 */
let holder: object | null = null;
/**
 * A face (owner 2026-11-03: "why are there users who don't have an avatar at all? fix it"): their picture, else their Tiger
 * drawn still in the round frame (as the World's usercard) — never a bare letter for someone who has a Tiger.
 */
function Face({ person, size = 20 }: { person: (Pick<Person, 'name' | 'avatar'> & Partial<Person>) | null; size?: number }) {
  // a picture that does not load (an AI player's empty one) is no picture: the Tiger, not a letter
  const [bad, setBad] = useState(false);
  useEffect(() => setBad(false), [person?.avatar]);
  if (person?.avatar && !bad) {
    // eslint-disable-next-line @next/next/no-img-element -- a small, already-sized avatar
    return <img src={person.avatar} alt="" width={size} height={size} onError={() => setBad(true)} className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  if (!person?.id) return <Avatar person={person} size={size} />;
  return (
    <span aria-hidden className="relative block shrink-0 overflow-hidden rounded-full bg-[#2b2420]" style={{ width: size, height: size }}>
      <LiveTiger userId={person.id} live={false} background={false} className="absolute inset-0 h-full w-full" />
    </span>
  );
}

export default function ChatDock() {
  const [me] = useState(() => ({}));
  const [mine, setMine] = useState(false);
  useEffect(() => {
    if (!holder) { holder = me; setMine(true); }
    return () => { if (holder === me) holder = null; };
  }, [me]);
  if (!mine) return null;
  return <Window />;
}

function Window() {
  const dock = useChat((s) => s.dock);
  const box = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(0);
  // A callback ref: the window is drawn through a portal a moment after this
  // renders, so it is measured when it actually appears.
  const ro = useRef<ResizeObserver | null>(null);
  const measure = useCallback((el: HTMLDivElement | null) => {
    ro.current?.disconnect();
    box.current = el;
    if (!el) return;
    setW(el.getBoundingClientRect().width);
    ro.current = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.current.observe(el);
  }, []);
  const inWorld = usePathname()?.startsWith('/world') ?? false;
  // Escape: out of full window first, then closed. In the World: closed at once, wherever the focus is ("Esc ✕").
  useEffect(() => {
    if (!dock.open) return;
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || (e.target as HTMLElement)?.closest?.('[data-chat-keep-esc]')) return;
      if (!inWorld && !box.current?.contains(document.activeElement)) return;
      if (inWorld) e.stopPropagation();
      setDock(dock.full && !inWorld ? { full: false } : { open: false, full: false });
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [dock.open, dock.full, inWorld]);
  const theme0 = useChat((s) => s.theme);
  // inside the World: the Games room's Gilded Arcade look (owner 2026-11-03: "change the chatbox theme to the theme we use in
  // the game home page") — always dark, obsidian and gold (components/chat/chat-ga.css)
  const theme = inWorld ? 'dark' : theme0;
  if (!dock.open) return null;
  const two = w >= 620;
  const showList = two || dock.view === 'list';
  const showPane = two || dock.view !== 'list';
  return (
    <Layer>
      {/* The window's own look: `.dark` turns it dark on a light site, `.gt-light` keeps it light on a dark one. */}
      <div className={(theme === 'dark' ? 'dark' : theme === 'light' ? 'gt-light' : '') + (inWorld ? ' ga-room tms-chat-ga' : '') || undefined}>
      <section ref={measure} role="dialog" aria-label="Chat" data-chat-ga={inWorld ? '' : undefined} style={theme === 'auto' ? undefined : { colorScheme: theme }}
               className={'fixed z-[105] flex flex-col overflow-hidden border border-ink-200 bg-white text-ink-900 shadow-[0_24px_70px_-12px_rgba(0,0,0,0.45)] dark:border-white/10 dark:bg-ink-900 dark:text-ink-50 ' +
                 (dock.full
                   ? 'inset-x-2 bottom-2 top-[64px] rounded-2xl sm:inset-x-4 sm:bottom-4'
                   : 'inset-0 sm:inset-auto sm:bottom-4 sm:right-4 sm:h-[min(620px,calc(100dvh-88px))] sm:w-[min(780px,calc(100vw-32px))] sm:rounded-2xl')}>
        <header className="flex h-12 shrink-0 items-center gap-1 border-b border-ink-100 pl-4 pr-2 dark:border-white/[0.08]">
          <h2 className="flex-1 text-[15px] font-bold">Chats</h2>
          {/* in the World: only the close button, "Esc ✕" (owner 2026-11-03) */}
          {inWorld ? (
            <button type="button" className="tms-chat-close" title="Close the chats (Esc)" aria-label="Close the chats" onClick={() => setDock({ open: false, full: false })}>
              <kbd>Esc</kbd><I d={ICON.close} className="h-[16px] w-[16px]" />
            </button>
          ) : <>
          <button type="button" className={BTN} title="New chat" aria-label="New chat" onClick={() => setDock({ view: 'new' })}><I d={ICON.plus} /></button>
          <button type="button" className={BTN} title="Create a group" aria-label="Create a group" onClick={() => setDock({ view: 'group' })}><I d={ICON.group} /></button>
          <button type="button" className={BTN + (dock.view === 'prefs' ? ' bg-ink-100 text-ink-900 dark:bg-white/[0.08] dark:text-white' : '')} title="Chat settings" aria-label="Chat settings" aria-pressed={dock.view === 'prefs'}
                  onClick={() => setDock({ view: dock.view === 'prefs' ? 'list' : 'prefs' })}><I d={ICON.gear} /></button>
          {/* the chat as its own page (components/messages/MessagesPage.tsx) */}
          <a href={dock.view === 'room' && dock.room ? `/messages?room=${dock.room}` : '/messages'} className={BTN} title="Open the Messages page" aria-label="Open the Messages page"><I d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" /></a>
          <button type="button" className={BTN + ' hidden sm:grid'} title={dock.full ? 'Back to the corner' : 'Full window'} aria-label={dock.full ? 'Back to the corner' : 'Full window'} onClick={() => setDock({ full: !dock.full })}>
            <I d={dock.full ? ICON.shrink : ICON.expand} />
          </button>
          <button type="button" className={BTN + ' hidden sm:grid'} title="Minimize" aria-label="Minimize" onClick={() => setDock({ open: false })}><I d={ICON.min} /></button>
          <button type="button" className={BTN} title="Close" aria-label="Close" onClick={() => setDock({ open: false, full: false })}><I d={ICON.close} /></button>
          </>}
        </header>
        <div className="flex min-h-0 flex-1">
          {showList && <List wide={two} />}
          {showPane && (
            <div className={'flex min-w-0 flex-1 flex-col ' + (two ? 'border-l border-ink-100 dark:border-white/[0.08]' : '')}>
              {dock.view === 'room' && dock.room ? <Conversation key={dock.room} roomId={dock.room} narrow={!two} />
                : dock.view === 'new' ? <NewChat narrow={!two} />
                : dock.view === 'group' ? <CreateGroup narrow={!two} />
                : dock.view === 'settings' && dock.room ? <Settings key={dock.room} roomId={dock.room} narrow={!two} />
                : dock.view === 'request' && dock.to ? <RequestComposer to={dock.to} narrow={!two} />
                : dock.view === 'prefs' ? <Prefs narrow={!two} />
                : <Welcome />}
            </div>
          )}
        </div>
      </section>
      </div>
    </Layer>
  );
}

function Welcome() {
  return (
    <div className="grid flex-1 place-items-center p-6 text-center">
      <div className="max-w-xs">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-accent-50 text-accent-600 dark:bg-accent-500/15 dark:text-accent-300"><I d={ICON.send} className="h-6 w-6" /></span>
        <p className="mt-3 text-[15px] font-bold">Pick a chat</p>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-500 dark:text-ink-400">Send anyone on Kharma a chat request. Once they accept it, you can chat freely.</p>
        <div className="mt-4 flex justify-center gap-2">
          <button type="button" className={PRIMARY} onClick={() => setDock({ view: 'new' })}>New chat</button>
          <button type="button" className={QUIET} onClick={() => setDock({ view: 'group' })}>Create a group</button>
        </div>
      </div>
    </div>
  );
}

function PaneHead({ title, sub, narrow, onBack, right, face }: { title: ReactNode; sub?: ReactNode; narrow: boolean; onBack?: () => void; right?: ReactNode; face?: ReactNode }) {
  return (
    <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-ink-100 px-3 dark:border-white/[0.08]">
      {narrow && <button type="button" className={BTN} aria-label="Back" onClick={onBack ?? (() => setDock({ view: 'list' }))}><I d={ICON.back} /></button>}
      {face}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14.5px] font-bold">{title}</p>
        {sub && <div className="truncate text-[12px] text-ink-500 dark:text-ink-400">{sub}</div>}
      </div>
      {right}
    </div>
  );
}

// ── The list ─────────────────────────────────────────────────────────────────

function List({ wide }: { wide: boolean }) {
  const rooms = useChat((s) => s.rooms);
  const dock = useChat((s) => s.dock);
  const requests = useChat((s) => s.requests);
  const [q, setQ] = useState('');
  useEffect(() => { if (!rooms) void refreshRooms(); }, [rooms]);
  const shown = useMemo(() => {
    const all = rooms ?? [];
    const tab = all.filter((r) => (dock.tab === 'requests' ? r.incoming : !r.incoming));
    const n = q.trim().toLowerCase();
    return n ? tab.filter((r) => roomTitle(r).toLowerCase().includes(n) || r.people.some((p) => p.name.toLowerCase().includes(n))) : tab;
  }, [rooms, dock.tab, q]);
  return (
    <div className={'flex min-h-0 flex-col ' + (wide ? 'w-[280px] shrink-0' : 'flex-1')}>
      <div className="flex gap-1 px-3 pt-2.5">
        {(['chats', 'requests'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setDock({ tab: t })} aria-pressed={dock.tab === t}
                  className={'flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold transition ' + (dock.tab === t ? 'bg-ink-900 text-white dark:bg-ink-100 dark:text-ink-900' : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-white/[0.06]')}>
            {t === 'chats' ? 'Chats' : 'Requests'}
            {t === 'requests' && requests > 0 && <span className="min-w-[18px] rounded-full bg-rose-500 px-1 text-center text-[10.5px] font-bold leading-[18px] text-white">{requests}</span>}
          </button>
        ))}
      </div>
      <div className="px-3 py-2">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats" aria-label="Search chats" className={INPUT + ' !rounded-full !py-1.5 text-[13px]'} />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2">
        {!rooms && Array.from({ length: 5 }, (_, i) => <li key={i} className="mx-3 my-2 h-12 animate-pulse rounded-xl bg-ink-100 dark:bg-white/[0.05]" />)}
        {rooms && !shown.length && (
          <li className="px-5 py-8 text-center text-[13px] text-ink-500 dark:text-ink-400">
            {dock.tab === 'requests' ? 'No chat requests.' : q ? 'No chats match.' : <>No chats yet. <button type="button" className="font-semibold text-accent-700 hover:underline dark:text-accent-300" onClick={() => setDock({ view: 'new' })}>Start one</button>.</>}
          </li>
        )}
        {shown.map((r) => {
          const on = dock.view === 'room' && dock.room === r.id;
          const preview = r.outgoing ? (r.state === 'declined' ? 'Request not accepted' : 'Waiting for them to accept')
            : r.last ? `${r.last.mine ? 'You: ' : r.kind === 'group' && r.last.kind === 'text' && r.last.authorName ? `${r.last.authorName}: ` : ''}${r.last.text}` : 'No messages yet';
          return (
            <li key={r.id}>
              <button type="button" onClick={() => openRoom(r.id)} aria-current={on || undefined}
                      className={'flex w-full items-center gap-3 px-3 py-2 text-left transition ' + (on ? 'bg-accent-50 dark:bg-accent-500/10' : 'hover:bg-ink-50 dark:hover:bg-white/[0.04]')}>
                <RoomFace room={r} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className={'min-w-0 truncate text-[14px] ' + (r.unread && !r.muted ? 'font-bold' : 'font-semibold')}>{roomTitle(r)}</span>
                    {r.bond && r.bond.games > 0 && <span title={bondText(r.bond)} className="shrink-0 self-center text-emerald-600 dark:text-emerald-400"><I d={ICON.coop} className="h-3.5 w-3.5" /></span>}
                    <span className="flex-1" />
                    {r.last && <span className="shrink-0 text-[11.5px] text-ink-400">{shortAgo(r.last.at)}</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={'min-w-0 flex-1 truncate text-[12.5px] ' + (r.unread && !r.muted ? 'font-semibold text-ink-800 dark:text-ink-100' : 'text-ink-500 dark:text-ink-400')}>{preview}</span>
                    {r.muted && <span className="text-[11px] text-ink-400" title="Muted">muted</span>}
                    {r.unread > 0 && !r.incoming && <span className={'min-w-[18px] rounded-full px-1 text-center text-[10.5px] font-bold leading-[18px] text-white ' + (r.muted ? 'bg-ink-400' : 'bg-rose-500')}>{r.unread > 99 ? '99+' : r.unread}</span>}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── A chat ───────────────────────────────────────────────────────────────────

const EMPTY: ChatMessage[] = [];

function Conversation({ roomId, narrow }: { roomId: string; narrow: boolean }) {
  const room = useChat((s) => s.rooms?.find((r) => r.id === roomId) ?? null);
  const list = useChat((s) => s.threads[roomId]?.list ?? EMPTY);
  const more = useChat((s) => s.threads[roomId]?.more ?? false);
  const loading = useChat((s) => s.threads[roomId]?.loading ?? true);
  const me = useChat((s) => s.me);
  const typing = useChat((s) => s.typing[roomId]);
  const [reply, setReply] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [menu, setMenu] = useState(false);
  const [err, setErr] = useState('');
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const prevH = useRef(0);

  useEffect(() => { void loadLatest(roomId); if (!room) void refreshRooms(); }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Stay at the bottom as messages arrive (unless reading older ones); keep the place when older ones load above.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (prevH.current && el.scrollTop < 80 && el.scrollHeight > prevH.current && !stick.current) el.scrollTop += el.scrollHeight - prevH.current;
    else if (stick.current) el.scrollTop = el.scrollHeight;
    prevH.current = el.scrollHeight;
  }, [list.length, typing]);
  const onScroll = () => {
    const el = scroller.current!;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (el.scrollTop < 80 && more && !loading) { prevH.current = el.scrollHeight; void loadOlder(roomId); }
  };

  if (!room) return <div className="grid flex-1 place-items-center text-[13px] text-ink-500">Loading…</div>;
  const other = room.kind === 'dm' ? room.people[0] : null;
  const bySeq = new Map(list.map((m) => [m.seq, m]));

  const act = async (fn: () => Promise<unknown>) => { setErr(''); setMenu(false); try { await fn(); await refreshRooms(); } catch (e) { setErr((e as Error).message); } };

  return (
    <>
      <PaneHead narrow={narrow} face={<RoomFace room={room} size={34} />}
                title={other ? <Link href={`/u/${encodeURIComponent(other.name)}`} className="hover:underline">{roomTitle(room)}</Link> : roomTitle(room)}
                sub={room.kind === 'group' ? `${room.memberCount} ${room.memberCount === 1 ? 'member' : 'members'}`
                  : room.bond && room.bond.games > 0 ? <BondChip bond={room.bond} className="mt-0.5" />
                  : room.muted ? 'Muted' : undefined}
                right={
                  <div className="relative">
                    <button type="button" className={BTN} aria-label="Chat options" aria-expanded={menu} onClick={() => setMenu((v) => !v)}><I d={ICON.more} /></button>
                    {menu && (
                      <div className="absolute right-0 top-full z-10 mt-1 w-52 rounded-xl border border-ink-200 bg-white p-1 shadow-xl dark:border-white/10 dark:bg-ink-900" onMouseLeave={() => setMenu(false)}>
                        {room.kind === 'group' && <MenuItem onClick={() => { setMenu(false); setDock({ view: 'settings', room: roomId }); }}>Group settings</MenuItem>}
                        {other && <MenuItem onClick={() => { setMenu(false); window.location.assign(`/u/${encodeURIComponent(other.name)}`); }}>View profile</MenuItem>}
                        <MenuItem onClick={() => act(() => api.mute(roomId, !room.muted))}>{room.muted ? 'Unmute' : 'Mute'}</MenuItem>
                        <MenuItem onClick={() => act(async () => { await api.leave(roomId); setDock({ view: 'list', room: null }); })}>{room.kind === 'group' ? 'Leave the group' : 'Hide this chat'}</MenuItem>
                        {other && <MenuItem danger onClick={async () => { if (await askConfirm({ title: `Block u/${other.name}?`, body: 'Neither of you can message the other.', ok: 'Block', danger: true })) void act(async () => { await api.block(other.id); setDock({ view: 'list', room: null }); }); }}>Block u/{other.name}</MenuItem>}
                      </div>
                    )}
                  </div>
                } />
      <div ref={scroller} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3">
        {more && <p className="py-2 text-center text-[12px] text-ink-400">{loading ? 'Loading earlier messages…' : 'Scroll up for earlier messages'}</p>}
        {!loading && !list.length && <p className="py-10 text-center text-[13px] text-ink-500">No messages yet. Say hi.</p>}
        {list.map((m, i) => {
          const prev = list[i - 1];
          const newDay = !prev || dayOf(prev.at) !== dayOf(m.at);
          const mine = m.author?.id === me;
          const grouped = !newDay && sameRun(prev, m);
          const runEnd = !sameRun(m, list[i + 1]);
          return (
            <div key={m.id}>
              {newDay && <p className="my-3 text-center text-[11.5px] font-semibold text-ink-400">{dayOf(m.at)}</p>}
              {m.kind === 'system'
                ? <p className="my-2 text-center text-[12px] text-ink-500 dark:text-ink-400">{m.text}</p>
                : <Bubble m={m} mine={mine} grouped={!!grouped} runEnd={runEnd} group={room.kind === 'group'} replied={m.replyTo ? bySeq.get(m.replyTo) ?? null : null}
                          seen={room.kind === 'dm' && mine && !m.pending && !m.failed && room.otherRead >= m.seq}
                          onReply={() => setReply(m)} onEdit={() => setEditing(m)} roomId={roomId} />}
            </div>
          );
        })}
        {typing && <p className="mt-1 text-[12px] italic text-ink-500 dark:text-ink-400">u/{typing.name} is typing…</p>}
      </div>
      {err && <p role="alert" className="px-4 pb-1 text-[12.5px] text-rose-600 dark:text-rose-300">{err}</p>}
      {room.incoming ? (
        <div className="border-t border-ink-100 p-3 dark:border-white/[0.08]">
          <p className="text-[13px] text-ink-600 dark:text-ink-300"><b>{roomTitle(room)}</b> wants to chat with you. Accept to reply; they will not know if you decline.</p>
          {room.bond && room.bond.games > 0 && <p className="mt-1.5"><BondChip bond={room.bond} /></p>}
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={PRIMARY} onClick={() => act(() => api.accept(roomId))}>Accept</button>
            <button type="button" className={QUIET} onClick={() => act(async () => { await api.decline(roomId); setDock({ view: 'list', room: null }); })}>Decline</button>
            {other && <button type="button" className={QUIET + ' !text-rose-600'} onClick={() => act(async () => { await api.block(other.id); setDock({ view: 'list', room: null }); })}>Block</button>}
          </div>
        </div>
      ) : room.outgoing ? (
        <p className="border-t border-ink-100 p-3 text-[13px] text-ink-500 dark:border-white/[0.08] dark:text-ink-400">
          {room.state === 'declined' ? 'They did not accept your chat request.' : 'Your chat request was sent. You can write more once they accept.'}
        </p>
      ) : (
        <Composer roomId={roomId} reply={reply} onReplyDone={() => setReply(null)} editing={editing} onEditDone={() => setEditing(null)} onError={setErr} />
      )}
    </>
  );
}

function MenuItem({ children, onClick, danger }: { children: ReactNode; onClick: () => void; danger?: boolean }) {
  return <button type="button" onClick={onClick} className={'block w-full rounded-lg px-3 py-2 text-left text-[13.5px] hover:bg-ink-100 dark:hover:bg-white/[0.06] ' + (danger ? 'text-rose-600 dark:text-rose-300' : '')}>{children}</button>;
}

function Bubble({ m, mine, grouped, runEnd, group, replied, seen, onReply, onEdit, roomId }: {
  m: ChatMessage; mine: boolean; grouped: boolean; runEnd: boolean; group: boolean; replied: ChatMessage | null; seen: boolean; onReply: () => void; onEdit: () => void; roomId: string;
}) {
  const [busy, setBusy] = useState(false);
  const tools = !m.pending && !m.failed && !m.deleted;
  // Translate (as in the world's post dialog): their message stays as written; translated, it shows the translation on hover.
  const t = usePostTranslation(`dm:${m.id}:${m.editedAt ?? ''}`, m.deleted || mine ? [] : [m.text], true);
  const trShown = t.status === 'done' && !t.done?.same;
  const trTip = t.status === 'busy' ? `Translating into ${t.langName}…` : t.status === 'error' ? `${t.error} — try again`
    : t.status === 'done' && t.done?.same ? `Already in ${t.langName}` : trShown ? (t.on ? 'Hide the translation (hover the message to read it)' : 'Show the translation')
    : `Translate into ${t.langName || 'your language'} — hover the message to read it (KHARMA like any translation)`;
  // Telegram's corner: the bubble beside the avatar (a run's last) gets the sharp
  // "tail" corner; bubbles inside a run are squared a little where they meet.
  const shape = 'rounded-2xl ' + (mine
    ? (grouped ? 'rounded-tr-md ' : '') + (runEnd ? 'rounded-br-[4px]' : 'rounded-br-md')
    : (grouped ? 'rounded-tl-md ' : '') + (runEnd ? 'rounded-bl-[4px]' : 'rounded-bl-md'));
  const tone = m.deleted ? 'text-ink-400 dark:text-ink-500' : mine ? 'text-white/75' : 'text-ink-500 dark:text-ink-400';
  // The stamp in the bubble's bottom-right corner: "edited", the time, and on
  // your own messages a clock while sending, ✓ sent, ✓✓ read (direct chats).
  const stamp = (
    <>
      {m.editedAt && !m.deleted && <span>edited</span>}
      <span>{clock(m.at)}</span>
      {mine && !m.failed && !m.deleted && (m.pending
        ? <I d={TICK.wait} className="h-3 w-3" />
        : <I d={seen ? TICK.read : TICK.sent} className={'h-[14px] ' + (seen ? 'w-[18px]' : 'w-[14px]')} />)}
    </>
  );
  return (
    <div className={'group flex items-end gap-2 ' + (mine ? 'flex-row-reverse' : '') + (grouped ? ' mt-0.5' : ' mt-2.5')}>
      {!mine && <span className="w-7 shrink-0">{runEnd && <Face person={m.author} size={28} />}</span>}
      <div className={'flex min-w-0 max-w-[78%] flex-col ' + (mine ? 'items-end' : 'items-start')}>
        <div title={new Date(m.at).toLocaleString()} className={'relative min-w-0 max-w-full px-3 pb-1.5 pt-1.5 text-[14px] leading-snug ' + shape + ' ' +
          (m.deleted ? 'italic text-ink-400 ring-1 ring-ink-200 dark:ring-white/10'
            : mine ? 'bg-accent-600 text-white [&_a]:text-white [&_a]:underline' : 'bg-ink-100 text-ink-900 dark:bg-white/[0.07] dark:text-ink-50') +
          (m.pending ? ' opacity-70' : '') + (m.failed ? ' ring-2 ring-rose-400' : '')}>
          {!mine && group && !grouped && <span className="block truncate text-[12.5px] font-semibold text-accent-700 dark:text-accent-300">u/{m.author?.name ?? '[deleted]'}</span>}
          {replied && (
            <span className={'mb-1 block max-w-full truncate rounded-md border-l-[3px] px-2 py-0.5 text-[12px] ' +
              (mine ? 'border-white/70 bg-white/15 text-white/90' : 'border-accent-500 bg-accent-500/10 text-ink-600 dark:text-ink-300')}>
              <b className="font-semibold">u/{replied.author?.name ?? '…'}</b> {replied.deleted ? 'deleted' : replied.text}
            </span>
          )}
          {m.deleted ? 'Message deleted' : <TrLine t={t} i={0} as="span"><RichText text={m.text} className="inline" /></TrLine>}
          {/* An invisible copy of the stamp holds room at the end of the last line, so the
              real one (pinned bottom-right) sits beside short text and drops below long text. */}
          <span aria-hidden className="invisible ml-2 inline-flex items-center gap-1 align-bottom text-[11px] leading-none">{stamp}</span>
          <span className={'absolute bottom-1.5 right-2.5 inline-flex items-center gap-1 text-[11px] leading-none ' + tone}>{stamp}</span>
        </div>
        {m.failed && (
          <span className="mt-0.5 flex items-center gap-1.5 px-1 text-[11px] text-ink-400">
            <span className="text-rose-500">Not sent.</span>
            <button type="button" className="font-semibold text-accent-700 hover:underline dark:text-accent-300" onClick={() => { dropFailed(roomId, m); void sendMessage(roomId, m.text, m.replyTo, m).catch(() => undefined); }}>Retry</button>
            <button type="button" className="hover:underline" onClick={() => dropFailed(roomId, m)}>Discard</button>
          </span>
        )}
      </div>
      {tools && (
        <div className="mb-0.5 flex shrink-0 gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
          <button type="button" className={BTN + ' !h-7 !w-7'} title="Reply" aria-label="Reply" onClick={onReply}><I d={ICON.reply} className="h-3.5 w-3.5" /></button>
          {mine ? (
            <>
              <button type="button" className={BTN + ' !h-7 !w-7 text-[11px] font-bold'} title="Edit" aria-label="Edit" onClick={onEdit}>✎</button>
              <button type="button" disabled={busy} className={BTN + ' !h-7 !w-7'} title="Delete" aria-label="Delete"
                      onClick={async () => { if (!(await askConfirm({ title: 'Delete this message for everyone?', ok: 'Delete', danger: true }))) return; setBusy(true); try { applyEdited(await api.remove(m.id)); } finally { setBusy(false); } }}>
                <I d={ICON.close} className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
            <button type="button" disabled={t.status === 'busy'} className={BTN + ' !h-7 !w-7' + (trShown && t.on ? ' text-accent-700 dark:text-accent-300' : t.status === 'error' ? ' text-rose-500' : '')}
                    title={trTip} aria-label={trTip} aria-pressed={trShown ? t.on : undefined}
                    onClick={() => (trShown ? t.toggle() : t.run())}>
              {t.status === 'busy' ? <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden /> : <TranslateGlyph className="h-3.5 w-3.5" />}
            </button>
            <button type="button" className={BTN + ' !h-7 !w-7 text-[11px]'} title="Report" aria-label="Report"
                    onClick={async () => { const why = prompt('Why are you reporting this message?'); if (why !== null) { await api.report(m.id, why).catch(() => undefined); alert('Thanks. The Kharma team will look at it.'); } }}>⚑</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Composer({ roomId, reply, onReplyDone, editing, onEditDone, onError }: {
  roomId: string; reply: ChatMessage | null; onReplyDone: () => void; editing: ChatMessage | null; onEditDone: () => void; onError: (e: string) => void;
}) {
  const [text, setText] = useState('');
  const area = useRef<HTMLTextAreaElement>(null);
  const draftKey = `kh_chat_draft_${roomId}`;
  useEffect(() => { try { setText(sessionStorage.getItem(draftKey) || ''); } catch { /* */ } area.current?.focus(); }, [draftKey]);
  useEffect(() => { if (editing) { setText(editing.text); area.current?.focus(); } }, [editing]);
  useEffect(() => { if (reply) area.current?.focus(); }, [reply]);
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [text]);
  const keep = (t: string) => { setText(t); try { if (!editing) sessionStorage.setItem(draftKey, t); } catch { /* */ } };
  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    onError('');
    if (editing) {
      try { applyEdited(await api.edit(editing.id, t)); onEditDone(); keep(''); } catch (e) { onError((e as Error).message); }
      return;
    }
    keep('');
    const r = reply?.seq ?? null;
    onReplyDone();
    try { await sendMessage(roomId, t, r); } catch (e) { onError(e instanceof ChatApiError ? e.message : 'Not sent.'); }
  };
  return (
    <div className="border-t border-ink-100 p-2.5 dark:border-white/[0.08]">
      {(reply || editing) && (
        <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-ink-50 px-2.5 py-1.5 text-[12px] text-ink-600 dark:bg-white/[0.04] dark:text-ink-300">
          <span className="min-w-0 flex-1 truncate">{editing ? 'Editing your message' : <>Replying to <b>u/{reply!.author?.name}</b>: {reply!.text}</>}</span>
          <button type="button" className="font-semibold hover:underline" onClick={() => { if (editing) { onEditDone(); keep(''); } else onReplyDone(); }}>Cancel</button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea ref={area} value={text} rows={1} maxLength={4000} data-chat-keep-esc={editing || reply ? '' : undefined}
                  onChange={(e) => { keep(e.target.value); if (e.target.value.trim()) sayTyping(roomId); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void submit(); }
                    if (e.key === 'Escape' && (editing || reply)) { e.stopPropagation(); if (editing) { onEditDone(); keep(''); } else onReplyDone(); }
                  }}
                  placeholder="Message" aria-label="Message"
                  className={INPUT + ' max-h-[140px] min-h-[40px] resize-none !rounded-2xl leading-snug'} />
        <button type="button" disabled={!text.trim()} onClick={() => void submit()} aria-label={editing ? 'Save' : 'Send'} title={editing ? 'Save' : 'Send (Enter)'}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent-600 text-white transition hover:bg-accent-700 disabled:opacity-40">
          <I d={editing ? 'M5 12l5 5L20 7' : ICON.send} />
        </button>
      </div>
      <p className="mt-1 px-1 text-[10.5px] text-ink-400">Enter to send · Shift+Enter for a new line</p>
    </div>
  );
}

// ── New chat, a request, a group ─────────────────────────────────────────────

function usePeople(q: string) {
  const [data, setData] = useState<Contact[] | null>(null);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => { api.contacts(q).then((d) => alive && setData(d)).catch(() => alive && setData([])); }, q ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return data;
}

function PersonRow({ p, onClick, right }: { p: Contact; onClick: () => void; right?: ReactNode }) {
  return (
    <li>
      <button type="button" onClick={onClick} className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-ink-50 dark:hover:bg-white/[0.04]">
        <Face person={p} size={34} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-semibold">u/{p.name}</span>
          {p.bond && p.bond.games > 0 && <BondChip bond={p.bond} className="mt-0.5" />}
        </span>
        {right}
      </button>
    </li>
  );
}

const STATUS: Record<string, { label: string; tone: string }> = {
  active: { label: 'Chat', tone: 'text-ink-500 dark:text-ink-400' },
  sent: { label: 'Requested', tone: 'text-ink-400' },
  received: { label: 'Wants to chat', tone: 'text-rose-600 dark:text-rose-300' },
  declined: { label: 'Not accepted', tone: 'text-ink-400' },
  none: { label: 'Request', tone: 'text-accent-700 dark:text-accent-300' },
};

/** New chat: anyone on Kharma. Your chats first, then people you played with, then anyone by name. */
function NewChat({ narrow }: { narrow: boolean }) {
  const [q, setQ] = useState('');
  const data = usePeople(q);
  const go = (p: Contact) => {
    if (p.roomId) { openRoom(p.roomId); void api.dm(p.id).catch(() => undefined); }   // shows a chat you had hidden
    else setDock({ view: 'request', to: p });
  };
  const chats = data?.filter((p) => p.chat === 'active') ?? [];
  const played = data?.filter((p) => p.chat !== 'active' && p.bond && p.bond.games > 0) ?? [];
  const rest = data?.filter((p) => p.chat !== 'active' && !(p.bond && p.bond.games > 0)) ?? [];
  const section = (title: string, list: Contact[]) => list.length > 0 && (
    <>
      <li className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-400">{title}</li>
      {list.map((p) => {
        const st = STATUS[p.chat ?? 'none'];
        return <PersonRow key={p.id} p={p} onClick={() => go(p)} right={<span className={'shrink-0 text-[12px] font-semibold ' + st.tone}>{st.label}</span>} />;
      })}
    </>
  );
  return (
    <>
      <PaneHead title="New chat" sub="Send anyone a chat request" narrow={narrow} />
      <div className="p-3 pb-1"><input autoFocus type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search anyone on Kharma by name" aria-label="Search people" className={INPUT} /></div>
      <ul className="min-h-0 flex-1 overflow-y-auto pb-3">
        {!data && <li className="px-4 py-3 text-[13px] text-ink-500">Loading…</li>}
        {data && (
          <>
            {section('Your chats', chats)}
            {section('Played together', played)}
            {section(q ? 'People on Kharma' : 'Others', rest)}
            {!data.length && <li className="px-4 py-6 text-center text-[13px] text-ink-500 dark:text-ink-400">{q.trim().length >= 2 ? 'Nobody by that name.' : 'Type a name to find anyone on Kharma.'}</li>}
            {!q && data.length > 0 && <li className="px-4 pt-3 text-[12px] text-ink-400">Type a name to find anyone else.</li>}
          </>
        )}
      </ul>
    </>
  );
}

function RequestComposer({ to, narrow }: { to: Person; narrow: boolean }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [bond, setBond] = useState<Bond | null>((to as Partial<Contact>).bond ?? null);
  useEffect(() => {
    let alive = true;
    // A chat already there (they asked you a moment ago, or you asked before): open it instead.
    api.peer(to.id).then((p) => { if (!alive) return; setBond(p.bond); if (p.roomId) openRoom(p.roomId); }).catch(() => undefined);
    return () => { alive = false; };
  }, [to.id]);
  const send = async () => {
    setBusy(true); setErr('');
    try { const r = await api.dm(to.id, text); await refreshRooms(); openRoom(r.roomId); } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  return (
    <>
      <PaneHead title={`Chat request to u/${to.name}`} sub={bond && bond.games > 0 ? <BondChip bond={bond} className="mt-0.5" /> : 'Your first chat together'}
                narrow={narrow} onBack={() => setDock({ view: 'new' })} face={<Face person={to} size={34} />} />
      <div className="flex flex-1 flex-col gap-3 p-4">
        <p className="text-[13px] leading-relaxed text-ink-600 dark:text-ink-300">
          Write a first message. u/{to.name} gets it as a chat request; once they accept it (or reply), you can chat freely.
          {bond && bond.games > 0 && <> They will see that you have played co-op together.</>}
        </p>
        <textarea autoFocus value={text} onChange={(e) => setText(e.target.value.slice(0, 4000))} rows={5} placeholder={`Hi ${to.name}, …`} className={INPUT}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim() && !busy) void send(); }} />
        {err && <p role="alert" className="text-[12.5px] text-rose-600">{err}</p>}
        <div><button type="button" disabled={busy || !text.trim()} className={PRIMARY} onClick={send}>{busy ? 'Sending…' : 'Send request'}</button></div>
      </div>
    </>
  );
}

/** People to put in a group: only people you chat with (nobody is added to a group by a stranger). */
function Pick({ selected, onToggle, exclude = [] }: { selected: string[]; onToggle: (p: Contact) => void; exclude?: string[] }) {
  const [q, setQ] = useState('');
  const data = usePeople(q);
  const people = (data ?? []).filter((p) => p.chat === 'active' && !exclude.includes(p.id));
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="px-4 pb-2"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people you chat with" className={INPUT} /></div>
      <ul className="min-h-0 flex-1 overflow-y-auto">
        {!data && <li className="px-4 py-3 text-[13px] text-ink-500">Loading…</li>}
        {data && !people.length && <li className="px-4 py-3 text-[12.5px] text-ink-500 dark:text-ink-400">You can add people you chat with. To add someone new, send them a chat request first (New chat).</li>}
        {people.map((p) => (
          <PersonRow key={p.id} p={p} onClick={() => onToggle(p)}
                     right={<span aria-hidden className={'grid h-5 w-5 shrink-0 place-items-center rounded-md border text-[12px] font-bold ' + (selected.includes(p.id) ? 'border-accent-600 bg-accent-600 text-white' : 'border-ink-300 dark:border-ink-600')}>{selected.includes(p.id) ? '✓' : ''}</span>} />
        ))}
      </ul>
    </div>
  );
}

/** Chat settings (⚙): the window's look, and the accounts you blocked. */
function Prefs({ narrow }: { narrow: boolean }) {
  const theme = useChat((s) => s.theme);
  const [blocked, setBlocked] = useState<Person[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.blocked().then(setBlocked).catch(() => setBlocked([])); }, []);
  const LOOKS: { id: ChatTheme; label: string; icon: string; note: string }[] = [
    { id: 'auto', label: 'Kharma', icon: ICON.auto, note: 'Follows the site' },
    { id: 'light', label: 'Light', icon: ICON.sun, note: 'Chat always light' },
    { id: 'dark', label: 'Dark', icon: ICON.moon, note: 'Chat always dark' },
  ];
  return (
    <>
      <PaneHead title="Chat settings" narrow={narrow} />
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink-400">Appearance</p>
        <div role="radiogroup" aria-label="Chat appearance" className="mt-2 grid grid-cols-3 gap-2">
          {LOOKS.map((l) => {
            const on = theme === l.id;
            return (
              <button key={l.id} type="button" role="radio" aria-checked={on} onClick={() => setChatTheme(l.id)}
                      className={'flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-center transition ' +
                        (on ? 'border-accent-500 bg-accent-50 ring-2 ring-accent-400/30 dark:border-accent-400 dark:bg-accent-500/10' : 'border-ink-200 hover:bg-ink-50 dark:border-white/10 dark:hover:bg-white/[0.04]')}>
                <span className={'grid h-9 w-9 place-items-center rounded-full ' + (l.id === 'light' ? 'bg-white text-amber-500 ring-1 ring-ink-200' : l.id === 'dark' ? 'bg-ink-900 text-sky-300 ring-1 ring-white/10' : 'bg-gradient-to-r from-white to-ink-900 text-ink-500 ring-1 ring-ink-200 dark:ring-white/10')}>
                  <I d={l.icon} className="h-[18px] w-[18px]" />
                </span>
                <span className="text-[13.5px] font-semibold">{l.label}</span>
                <span className="text-[11.5px] leading-tight text-ink-500 dark:text-ink-400">{l.note}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[12px] text-ink-500 dark:text-ink-400">Kharma (the default) follows the site&apos;s own light or dark mode. Light and Dark change only the chat window, in this browser.</p>

        <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.1em] text-ink-400">Blocked accounts</p>
        {!blocked && <p className="mt-2 text-[13px] text-ink-500">Loading…</p>}
        {blocked && !blocked.length && <p className="mt-2 text-[13px] text-ink-500 dark:text-ink-400">You have not blocked anyone. Blocked people cannot send you messages or chat requests.</p>}
        <ul className="mt-1">
          {blocked?.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-1.5">
              <Face person={p} size={32} />
              <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">u/{p.name}</span>
              <button type="button" className="text-[12.5px] font-semibold text-accent-700 hover:underline dark:text-accent-300"
                      onClick={async () => { setErr(''); try { await api.unblock(p.id); setBlocked((b) => b?.filter((x) => x.id !== p.id) ?? null); } catch (e) { setErr((e as Error).message); } }}>Unblock</button>
            </li>
          ))}
        </ul>
        {err && <p role="alert" className="mt-2 text-[12.5px] text-rose-600">{err}</p>}
      </div>
    </>
  );
}

function CreateGroup({ narrow }: { narrow: boolean }) {
  const [name, setName] = useState('');
  const [sel, setSel] = useState<Person[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const create = async () => {
    setBusy(true); setErr('');
    try { const r = await api.group(name, sel.map((p) => p.id)); await refreshRooms(); openRoom(r.roomId); } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  return (
    <>
      <PaneHead title="Create a group" sub={`${sel.length} selected · up to 49`} narrow={narrow} />
      <div className="p-4 pb-2">
        <input autoFocus value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} placeholder="Group name" aria-label="Group name" className={INPUT} />
        {sel.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {sel.map((p) => <button key={p.id} type="button" onClick={() => setSel((s) => s.filter((x) => x.id !== p.id))} className="rounded-full bg-accent-50 px-2.5 py-1 text-[12px] font-semibold text-accent-800 dark:bg-accent-500/15 dark:text-accent-200">u/{p.name} ✕</button>)}
          </div>
        )}
      </div>
      <Pick selected={sel.map((p) => p.id)} onToggle={(p) => setSel((s) => (s.some((x) => x.id === p.id) ? s.filter((x) => x.id !== p.id) : s.length >= 49 ? s : [...s, p]))} />
      {err && <p role="alert" className="px-4 pt-2 text-[12.5px] text-rose-600">{err}</p>}
      <div className="border-t border-ink-100 p-3 dark:border-white/[0.08]">
        <button type="button" disabled={busy || !name.trim() || !sel.length} className={PRIMARY + ' w-full'} onClick={create}>{busy ? 'Creating…' : 'Create group'}</button>
      </div>
    </>
  );
}

function Settings({ roomId, narrow }: { roomId: string; narrow: boolean }) {
  const me = useChat((s) => s.me);
  const [room, setRoom] = useState<ChatRoom | null>(null);
  const [name, setName] = useState('');
  const [adding, setAdding] = useState<Person[] | null>(null);
  const [err, setErr] = useState('');
  const load = async () => { try { const r = await api.room(roomId); setRoom(r); setName(r.name); } catch (e) { setErr((e as Error).message); } };
  useEffect(() => { void load(); }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn: () => Promise<unknown>) => { setErr(''); try { await fn(); await load(); await refreshRooms(); } catch (e) { setErr((e as Error).message); } };
  if (!room) return <div className="grid flex-1 place-items-center text-[13px] text-ink-500">{err || 'Loading…'}</div>;
  const admin = room.role === 'owner' || room.role === 'admin';
  const back = () => setDock({ view: 'room', room: roomId });
  if (adding) {
    return (
      <>
        <PaneHead title="Add people" sub={`${adding.length} selected`} narrow onBack={() => setAdding(null)} />
        <Pick selected={adding.map((p) => p.id)} exclude={(room.members ?? []).map((m) => m.person.id)} onToggle={(p) => setAdding((s) => (s!.some((x) => x.id === p.id) ? s!.filter((x) => x.id !== p.id) : [...s!, p]))} />
        {err && <p role="alert" className="px-4 pt-2 text-[12.5px] text-rose-600">{err}</p>}
        <div className="border-t border-ink-100 p-3 dark:border-white/[0.08]">
          <button type="button" disabled={!adding.length} className={PRIMARY + ' w-full'} onClick={() => act(async () => { await api.addMembers(roomId, adding.map((p) => p.id)); setAdding(null); })}>Add</button>
        </div>
      </>
    );
  }
  return (
    <>
      <PaneHead title="Group settings" sub={roomTitle(room)} narrow onBack={back} />
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <label className="block text-[12px] font-semibold text-ink-500">Name
          <div className="mt-1 flex gap-2">
            <input value={name} disabled={!admin} onChange={(e) => setName(e.target.value.slice(0, 60))} className={INPUT} />
            {admin && name.trim() && name !== room.name && <button type="button" className={PRIMARY} onClick={() => act(() => api.rename(roomId, name))}>Save</button>}
          </div>
        </label>
        <div className="mt-5 flex items-center justify-between">
          <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink-400">{room.members?.length ?? 0} members</p>
          {admin && <button type="button" className="text-[13px] font-semibold text-accent-700 hover:underline dark:text-accent-300" onClick={() => setAdding([])}>Add people</button>}
        </div>
        <ul className="mt-1">
          {(room.members ?? []).map(({ person: p, role }) => (
            <li key={p.id} className="flex items-center gap-3 py-1.5">
              <Face person={p} size={32} />
              <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">u/{p.name}{p.id === me && ' (you)'}</span>
              {role !== 'member' && <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-semibold capitalize text-ink-600 dark:bg-white/[0.06] dark:text-ink-300">{role}</span>}
              {room.role === 'owner' && p.id !== me && role !== 'owner' && (
                <button type="button" className="text-[12px] font-semibold text-ink-600 hover:underline dark:text-ink-300" onClick={() => act(() => api.setAdmin(roomId, p.id, role !== 'admin'))}>{role === 'admin' ? 'Remove admin' : 'Make admin'}</button>
              )}
              {admin && p.id !== me && role !== 'owner' && !(role === 'admin' && room.role !== 'owner') && (
                <button type="button" className="text-[12px] font-semibold text-rose-600 hover:underline" onClick={async () => { if (await askConfirm({ title: `Remove u/${p.name} from the group?`, ok: 'Remove', danger: true })) void act(() => api.removeMember(roomId, p.id)); }}>Remove</button>
              )}
            </li>
          ))}
        </ul>
        {err && <p role="alert" className="mt-3 text-[12.5px] text-rose-600">{err}</p>}
        <button type="button" className={QUIET + ' mt-6 !text-rose-600'} onClick={async () => { if (await askConfirm({ title: 'Leave this group?', ok: 'Leave', danger: true })) void act(async () => { await api.leave(roomId); setDock({ view: 'list', room: null }); }); }}>Leave the group</button>
      </div>
    </>
  );
}
